import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  MailOutlineRounded as MailIcon,
  LockOutlined as LockIcon,
  ShieldOutlined as ShieldIcon,
  DevicesRounded as DevicesIcon,
  BoltRounded as BoltIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import LoginDiagnostics from '../components/LoginDiagnostics';
import { BRAND } from '../theme';

const FEATURES = [
  { icon: <BoltIcon />, title: 'Live operations', text: 'Customers, appointments, and assessments in one place.' },
  { icon: <DevicesIcon />, title: 'Works everywhere', text: 'Designed for the office, the truck, and the roof.' },
  { icon: <ShieldIcon />, title: 'Role-based access', text: 'Every teammate sees exactly what they need.' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const emailInvalid = email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const result = await login(email, password);

      if (!result.success) {
        setError(result.error);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to login. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: 'background.default',
      }}
    >
      {/* Brand panel */}
      <Box
        sx={{
          flex: { md: '1 1 48%' },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'center', md: 'space-between' },
          color: '#E2E8F0',
          background: (theme) => theme.custom.gradients.night,
          px: { xs: 3, sm: 5, md: 7 },
          py: { xs: 3.5, md: 6 },
          minHeight: { xs: 'auto', md: '100dvh' },
        }}
      >
        {/* Decorative glows */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(600px 300px at 85% 110%, ${alpha(BRAND.orange, 0.22)}, transparent 65%)`,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.05,
            backgroundImage:
              'repeating-linear-gradient(-45deg, #fff 0px, #fff 1px, transparent 1px, transparent 14px)',
          }}
        />

        {/* Logo row */}
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: '#fff',
              boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/Logo_Icon-Black_Orange.png`}
              alt="Skyline logo"
              sx={{ width: 32, height: 32, objectFit: 'contain' }}
            />
          </Box>
          <Box>
            <Typography
              sx={{ fontFamily: '"Sora", sans-serif', fontWeight: 800, fontSize: '1.2rem', color: '#fff', lineHeight: 1.2 }}
            >
              SKYLINE
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#E2E8F0', 0.6), letterSpacing: '0.1em' }}>
              CONSTRUCTION & HOMES
            </Typography>
          </Box>
        </Stack>

        {/* Headline + features (hidden on small screens to keep login above the fold) */}
        <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' }, maxWidth: 460 }}>
          <Typography
            variant="h1"
            sx={{ color: '#fff', fontSize: 'clamp(2rem, 3.2vw, 2.9rem)', mb: 2 }}
          >
            Run the whole operation from one command center.
          </Typography>
          <Typography sx={{ color: alpha('#E2E8F0', 0.72), fontSize: '1rem', mb: 4.5 }}>
            Sign in to manage customers, emergency requests, appointments, and more.
          </Typography>

          <Stack spacing={2.5}>
            {FEATURES.map((feature) => (
              <Stack key={feature.title} direction="row" spacing={2} alignItems="flex-start">
                <Box
                  aria-hidden
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    color: BRAND.orangeSoft,
                    backgroundColor: alpha(BRAND.orange, 0.14),
                    border: `1px solid ${alpha(BRAND.orange, 0.25)}`,
                    '& svg': { fontSize: 21 },
                  }}
                >
                  {feature.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9375rem' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#E2E8F0', 0.6) }}>
                    {feature.text}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography
          variant="caption"
          sx={{ position: 'relative', color: alpha('#E2E8F0', 0.45), display: { xs: 'none', md: 'block' } }}
        >
          © {new Date().getFullYear()} Skyline Construction & Homes
        </Typography>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: { md: '1 1 52%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 6 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 420 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.7rem', md: '2rem' }, mb: 1 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Sign in to access the admin dashboard.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <TextField
              required
              fullWidth
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              error={emailInvalid}
              helperText={emailInvalid ? 'Please enter a valid email address' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isSubmitting}
              sx={{ py: 1.5 }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Stack>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <LoginDiagnostics />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
